// src/web/qaBar.js
//
// On-screen QA bar for testing engine options without editing URLs.
// Enabled with ?qa (forwarded automatically from goggle.html?qa by the host
// loader). Most engine flags are read once at boot, so every toggle rewrites
// the page's query string and reloads the SDK page (inside the iframe only —
// the store page is untouched). The current flag set is shown so a screenshot
// tells us exactly what configuration produced it.

const TOGGLES = [
  // [label, param, values (cycled), default, hint]
  ['solver',   'solver',    ['hybrid', 'pnp', 'mp'],           'hybrid', 'hybrid = MediaPipe rotation + our depth · pnp = full 6-DoF · mp = MediaPipe only'],
  ['lead',     'lead',      ['0', '20', '30', '45', '60'],     '0',    'motion prediction, ms'],
  ['track',    'trackmode', ['video', 'image'],                'video','image = no MediaPipe internal smoothing'],
  ['dfov',     'dfov',      ['74', '62', '85', '100', '120'],  '74',   'assumed diagonal FOV of the camera'],
  ['seat',     'noautoseat',['on', 'off'],                     'on',   'auto nose seating'],
  ['nose',     'noseadapt', ['on', 'off'],                     'on',   'per-user nose depth (item 2)'],
  ['temples',  'temples',   ['on', 'off'],                     'on',   'temple splay to head width'],
  ['realseat', 'realseat',  ['on', 'off'],                     'on',   'seat frame against the real face (not the average)'],
  ['lens',     'lensopacity', ['0.45', '0.25', '0.6', '0.8'],  '0.45', 'lens tint opacity'],
  ['delegate', 'delegate',  ['gpu', 'cpu'],                    'gpu',  'MediaPipe compute delegate'],
  ['scale',    'truescale', ['off', 'on'],                     'off',  'true-to-scale from PD'],
  ['filter',   'nofilter',  ['on', 'off'],                     'on',   'our pose smoother'],
  ['lock',     'framelock', ['on', 'off'],                     'on',   'frame-locked background'],
  ['res',      'res',       ['1440', '720', '1080'],           '1440', 'render height cap'],
  ['occ dbg',  'occdebug',  ['off', 'on'],                     'off',  'show occluder surface'],
  ['stats',    'lagdiag',   ['off', 'on'],                     'off',  'fps / solver overlay'],
];
// params whose presence (not value) means "off"/"on"
const PRESENCE_OFF = { noautoseat: 'off', nofilter: 'off' };
const PRESENCE_ON  = { occdebug: 'on', lagdiag: 'on', panel: 'on' };

function currentValue(qs, param, def) {
  if (param in PRESENCE_OFF) return qs.has(param) ? 'off' : 'on';
  if (param in PRESENCE_ON)  return qs.has(param) ? 'on' : 'off';
  const v = qs.get(param);
  return v == null || v === '' ? def : v;
}

function setValue(qs, param, val, def) {
  if (param in PRESENCE_OFF) { if (val === 'off') qs.set(param, ''); else qs.delete(param); return; }
  if (param in PRESENCE_ON)  { if (val === 'on')  qs.set(param, ''); else qs.delete(param); return; }
  if (val === def) qs.delete(param); else qs.set(param, val);
}

export function mountQaBar({ onPanel } = {}) {
  const qs = new URLSearchParams(window.location.search);
  if (!qs.has('qa')) return null;

  const bar = document.createElement('div');
  bar.id = 'qa-bar';
  bar.style.cssText = [
    'position:absolute;left:0;right:0;top:52px;z-index:40;display:flex;flex-wrap:wrap;gap:4px;',
    'padding:6px 8px;background:rgba(0,0,0,.55);backdrop-filter:blur(4px);',
    'font:11px ui-monospace,monospace;color:#fff;pointer-events:auto'
  ].join('');

  const mk = (label, value, hint, onClick, active) => {
    const b = document.createElement('button');
    b.textContent = value == null ? label : `${label}: ${value}`;
    b.title = hint || '';
    b.style.cssText = `border:1px solid rgba(255,255,255,.25);border-radius:999px;padding:3px 8px;cursor:pointer;font:inherit;` +
      `background:${active ? '#00A8B5' : 'rgba(255,255,255,.1)'};color:#fff`;
    b.onclick = onClick;
    bar.appendChild(b);
    return b;
  };

  for (const [label, param, values, def, hint] of TOGGLES) {
    const cur = currentValue(qs, param, def);
    mk(label, cur, hint, () => {
      const i = values.indexOf(cur);
      const next = values[(i + 1) % values.length];
      const q = new URLSearchParams(window.location.search);
      setValue(q, param, next, def);
      window.location.search = q.toString();
    }, cur !== def);
  }
  mk('panel', null, 'tuning sliders', () => onPanel?.(), false);
  mk('reset', null, 'back to defaults (keeps qa + embed)', () => {
    const q = new URLSearchParams();
    q.set('qa', '');
    for (const k of ['embed', 'origin']) if (qs.has(k)) q.set(k, qs.get(k));
    window.location.search = q.toString();
  }, false);
  mk('copy flags', null, 'copy the current flag set to clipboard', (ev) => {
    const flags = [...qs.entries()].filter(([k]) => !['embed', 'origin', 'qa'].includes(k))
      .map(([k, v]) => v === '' ? k : `${k}=${v}`).join('&') || '(defaults)';
    navigator.clipboard?.writeText(flags);
    ev.target.textContent = 'copied: ' + flags;
  }, false);

  document.getElementById('stage').appendChild(bar);
  return bar;
}
